import {
    $applyNodeReplacement,
    ElementNode,
    LexicalNode,
    NodeKey,
    SerializedElementNode,
    Spread,
    DOMExportOutput,
    DOMConversionMap,
    DOMConversionOutput,
    RangeSelection,
    ElementFormatType
} from "lexical"
import { $createDirectionalParagraphNode } from "./DirectionalParagraphNode"

export type SerializedBoxColorNode = Spread<
    {
        type: "box-color"
        version: 1
        backgroundColor: string
        padding: string
        direction: "ltr" | "rtl"
    },
    SerializedElementNode
>

function convertBoxColorElement(domNode: Node): null | DOMConversionOutput {
    const element = domNode as HTMLElement
    const backgroundColor = element.style.getPropertyValue("--box-color") || "transparent"
    const padding = element.style.getPropertyValue("--box-padding") || "10px"
    const node = $createBoxColorNode(backgroundColor, padding)
    return { node }
}

export class BoxColorNode extends ElementNode {
    __backgroundColor: string
    __padding: string
    __direction: "ltr" | "rtl"

    constructor(backgroundColor: string, padding: string, direction: "ltr" | "rtl" = "ltr", key?: NodeKey) {
        super(key)
        this.__backgroundColor = backgroundColor
        this.__padding = padding
        this.__direction = direction
    }

    static getType(): string {
        return "box-color"
    }

    static clone(node: BoxColorNode): BoxColorNode {
        return new BoxColorNode(node.__backgroundColor, node.__padding, node.__direction, node.__key)
    }

    createDOM(): HTMLElement {
        const div = document.createElement("p")
        div.className = "box-color-node"
        div.style.backgroundColor = this.__backgroundColor
        div.style.padding = this.__padding
        div.style.borderRadius = "4px"

        // Get direction - first check this node's direction
        let direction = this.__direction

        // If no direction on this node, try to inherit from parent
        if (!direction) {
            const parent = this.getParent()
            if (parent && "getDirection" in parent && typeof parent.getDirection === "function") {
                try {
                    const parentDirection = parent.getDirection()
                    if (parentDirection === "rtl" || parentDirection === "ltr") {
                        direction = parentDirection
                    }
                } catch (e) {
                    // Ignore errors if getDirection is not available
                }
            }
        }

        // Apply direction if available
        if (direction) {
            div.dir = direction
            div.style.direction = direction

            // Always respect the format type for text alignment
            const format = this.getFormatType()

            // Apply text alignment based on format
            if (format === "left" || format === "right" || format === "center" || format === "justify") {
                div.style.textAlign = format
            } else {
                // Only use direction-based alignment as fallback
                div.style.textAlign = direction === "rtl" ? "right" : "left"
            }
        }

        div.style.unicodeBidi = "embed"
        return div
    }

    remove(): void {
        const parent = this.getParentOrThrow()
        const children = this.getChildren()

        children.forEach((child) => {
            if (parent) {
                parent.append(child)
            }
        })

        super.remove()
    }

    updateDOM(prevNode: BoxColorNode, dom: HTMLElement): boolean {
        const hasBgColorChanged = prevNode.__backgroundColor !== this.__backgroundColor
        const hasPaddingChanged = prevNode.__padding !== this.__padding
        const hasDirectionChanged = prevNode.__direction !== this.__direction

        if (hasBgColorChanged) {
            dom.style.backgroundColor = this.__backgroundColor
        }
        if (hasPaddingChanged) {
            dom.style.padding = this.__padding
        }
        if (hasDirectionChanged) {
            dom.dir = this.__direction
            dom.style.direction = this.__direction

            // Always respect the format type for text alignment
            const format = this.getFormatType()

            // Apply text alignment based on format
            if (format === "left" || format === "right" || format === "center" || format === "justify") {
                dom.style.textAlign = format
            } else {
                // Only use direction-based alignment as fallback
                dom.style.textAlign = this.__direction === "rtl" ? "right" : "left"
            }
        }

        return hasBgColorChanged || hasPaddingChanged || hasDirectionChanged
    }

    static importJSON(serializedNode: SerializedBoxColorNode): BoxColorNode {
        const node = $createBoxColorNode(
            serializedNode.backgroundColor,
            serializedNode.padding,
            serializedNode.direction
        )
        node.setFormat(serializedNode.format)
        node.setIndent(serializedNode.indent)
        node.setDirection(serializedNode.direction)
        return node
    }

    exportJSON(): SerializedBoxColorNode {
        return {
            ...super.exportJSON(),
            backgroundColor: this.__backgroundColor,
            padding: this.__padding,
            direction: this.__direction,
            type: "box-color",
            version: 1
        }
    }

    static importDOM(): DOMConversionMap | null {
        return {
            div: (node: Node) => {
                const element = node as HTMLElement
                if (element.className === "box-color") {
                    return {
                        conversion: convertBoxColorElement,
                        priority: 0
                    }
                }
                return null
            }
        }
    }

    // BoxColor only needs color
    setBackgroundColor(color: string): void {
        const writable = this.getWritable()
        writable.__backgroundColor = color
    }

    setPadding(padding: string): void {
        const writable = this.getWritable()
        writable.__padding = padding
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement("p")
        element.className = "box-color-node"
        element.style.backgroundColor = this.__backgroundColor
        element.style.padding = this.__padding
        element.style.borderRadius = "4px"

        // Get direction - first check this node's direction
        let direction = this.__direction

        // If no direction on this node, try to inherit from parent
        if (!direction) {
            const parent = this.getParent()
            if (parent && "getDirection" in parent && typeof parent.getDirection === "function") {
                try {
                    const parentDirection = parent.getDirection()
                    if (parentDirection === "rtl" || parentDirection === "ltr") {
                        direction = parentDirection
                    }
                } catch (e) {
                    // Ignore errors if getDirection is not available
                }
            }
        }

        // Apply direction if available
        if (direction) {
            element.dir = direction
            element.style.direction = direction

            // Always respect the format type for text alignment
            const format = this.getFormatType()

            // Apply text alignment based on format
            if (format === "left" || format === "right" || format === "center" || format === "justify") {
                element.style.textAlign = format
            } else {
                // Only use direction-based alignment as fallback
                element.style.textAlign = direction === "rtl" ? "right" : "left"
            }

            element.style.unicodeBidi = "embed"
        } else {
            // Copy format (alignment) if no direction
            const format = this.getFormat()
            if (format === 1) {
                element.style.textAlign = "left"
            } else if (format === 2) {
                element.style.textAlign = "right"
            } else if (format === 3) {
                element.style.textAlign = "center"
            } else if (format === 4) {
                element.style.textAlign = "justify"
            }
        }

        return { element }
    }

    isInline(): boolean {
        return false
    }

    isEmpty(): boolean {
        return this.getTextContent().trim().length === 0
    }

    getBackgroundColor(): string {
        return this.__backgroundColor
    }

    insertNewAfter(selection: RangeSelection, restoreSelection?: boolean): LexicalNode | null {
        // Get direction from current node or try to get from parent
        let direction = this.__direction

        // If no direction on this node, try to inherit from parent
        if (!direction) {
            const parent = this.getParent()
            if (parent && "getDirection" in parent && typeof parent.getDirection === "function") {
                try {
                    const parentDirection = parent.getDirection()
                    if (parentDirection === "rtl" || parentDirection === "ltr") {
                        direction = parentDirection
                    }
                } catch (e) {
                    // Ignore errors if getDirection is not available
                }
            }
        }

        // Create new paragraph with inherited direction, fallback to ltr if no direction found
        const newPara = $createDirectionalParagraphNode(direction || "ltr")

        // Copy format from current node
        newPara.setFormat(this.__format as unknown as ElementFormatType)

        // Set text alignment based on direction
        if (direction === "rtl") {
            newPara.setFormat("right" as ElementFormatType)
        } else {
            newPara.setFormat("left" as ElementFormatType)
        }

        // Insert after current node
        this.insertAfter(newPara, restoreSelection)
        return newPara
    }

    insertNewBefore(): ElementNode {
        // Get direction from current node or try to get from parent
        let direction = this.__direction

        // If no direction on this node, try to inherit from parent
        if (!direction) {
            const parent = this.getParent()
            if (parent && "getDirection" in parent && typeof parent.getDirection === "function") {
                try {
                    const parentDirection = parent.getDirection()
                    if (parentDirection === "rtl" || parentDirection === "ltr") {
                        direction = parentDirection
                    }
                } catch (e) {
                    // Ignore errors if getDirection is not available
                }
            }
        }

        // Create new paragraph with inherited direction
        const paragraph = $createDirectionalParagraphNode(direction || "ltr")

        // Copy format and set alignment based on direction
        if (direction === "rtl") {
            paragraph.setFormat("right" as ElementFormatType)
        } else {
            paragraph.setFormat("left" as ElementFormatType)
        }

        this.append(paragraph)
        return paragraph
    }

    // Set direction explicitly
    setDirection(direction: "ltr" | "rtl"): this {
        const self = this.getWritable()
        self.__direction = direction
        return this
    }

    getDirection(): "ltr" | "rtl" {
        return this.__direction
    }
}

export function $createBoxColorNode(
    backgroundColor: string,
    padding: string,
    direction: "ltr" | "rtl" = "ltr"
): BoxColorNode {
    return $applyNodeReplacement(new BoxColorNode(backgroundColor, padding, direction))
}

export function $isBoxColorNode(node: LexicalNode | null | undefined): node is BoxColorNode {
    return node instanceof BoxColorNode
}
